import { useRef } from "react";
import { cn } from "@/lib/utils";
import { FiChevronLeft, FiChevronRight } from "@/lib/icons";
import type { AustralianState } from "../_types/calculator.types";
import { AUSTRALIAN_STATES } from "../_types/calculator.types";

interface StateSelectorProps {
  value: AustralianState;
  onChange: (state: AustralianState) => void;
}

/**
 * State selector tabs for Australian states/territories
 * With arrow navigation on mobile
 */
export function StateSelector({ value, onChange }: StateSelectorProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const currentIndex = AUSTRALIAN_STATES.indexOf(value);

  const goToPrev = () => {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : AUSTRALIAN_STATES.length - 1;
    onChange(AUSTRALIAN_STATES[prevIndex]);
  };

  const goToNext = () => {
    const nextIndex = currentIndex < AUSTRALIAN_STATES.length - 1 ? currentIndex + 1 : 0;
    onChange(AUSTRALIAN_STATES[nextIndex]);
  };

  return (
    <div className="relative border-b border-border">
      {/* Mobile: Arrow navigation */}
      <div className="flex md:hidden items-center">
        <button
          onClick={goToPrev}
          className="flex-shrink-0 p-3 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          aria-label="Previous state"
        >
          <FiChevronLeft className="size-5" />
        </button>

        <div
          ref={scrollRef}
          className="flex-1 flex overflow-x-auto scrollbar-hide"
        >
          {AUSTRALIAN_STATES.map((state) => (
            <button
              key={state}
              onClick={() => onChange(state)}
              className={cn(
                "flex-1 min-w-[50px] px-3 py-3 text-sm font-medium transition-all duration-200",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
                value === state
                  ? "text-primary-foreground bg-primary"
                  : "text-muted-foreground"
              )}
              aria-pressed={value === state}
              aria-label={`Select ${state}`}
            >
              {state}
            </button>
          ))}
        </div>

        <button
          onClick={goToNext}
          className="flex-shrink-0 p-3 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          aria-label="Next state"
        >
          <FiChevronRight className="size-5" />
        </button>
      </div>

      {/* Desktop: Full tabs */}
      <div className="hidden md:flex">
        {AUSTRALIAN_STATES.map((state) => (
          <button
            key={state}
            onClick={() => onChange(state)}
            className={cn(
              "flex-1 min-w-[60px] px-4 lg:px-6 py-4 text-sm font-medium transition-all duration-200 relative",
              "hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset",
              value === state
                ? "text-primary-foreground bg-primary"
                : "text-muted-foreground hover:text-foreground"
            )}
            aria-pressed={value === state}
            aria-label={`Select ${state}`}
          >
            {state}
          </button>
        ))}
      </div>
    </div>
  );
}
